import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, Accordion, AccordionSummary, AccordionDetails, FormControl, InputLabel, Select, MenuItem, Alert, CircularProgress, FormHelperText } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

import CustomerSearchSelectAsync, { isCreditHoldCustomer } from "../../../../../components/CustomerSearchSelectAsync";
import ButtonLoader from "../../../../../components/ButtonLoader/index.js";
import OrderUserAssignment from "../orders-single/order-components/orderUserAssignment/OrderUserAssignment";
import { AuthContext } from "../../../../../helpers/AuthContext";
import { getNextProjectNumber } from "../../../../../services/projectNumbers";
import { createCustomerContact, getClientContacts } from "../../../../../services/customer";
import CustomerContactDialog from "../../../../../components/CustomerContactDialog";
import useWorkflowDisplaySettings from "../../../../../hooks/useWorkflowDisplaySettings";
import ProjectCustomerEmailNotice from "../ProjectCustomerEmailNotice";
import {
  fetchClientContactOptions,
  getCustomerBusinessName,
  getCustomerChangeValues,
  normalizeCreatedClientContact,
  withOptionalClientContact,
} from "../clientContactSelection";

// Keep your existing EVENT_TYPES + StagePicker (import if you split them)
// import OrderUserAssignment from "./order-components/orderUserAssignment/OrderUserAssignment";

const EVENT_TYPES = [
  "Advertising","Awards","Camp clothing","Celebration","Client appreciation","Company branding","Company offsite",
  "Conference","Design","Donor gift","Employee appreciation","Evenet promotion","Fundraiser","Gift with purchase",
  "Golf tournament","Holiday event","Indvidiaul","Internal use","Mail out","On-premise","Online store","Other PPE",
  "Product launch","Program","Public relations","Recruitment","Retail merchandise","Sales tool","Samples","School clothing",
  "School orientation","Season celebration","Spirit wear","Sporting event","Team building","Thank-you gift","Trade show",
  "Travel","Uniforms","Virtual","Wedding","Wellness program"
];

const STAGE_CHOICES = [
  { key: "opportunity",  label: "Opportunity",  value: "Quote Requested",     img: "/project-general-images/opportunity.png" },
  { key: "waiting",      label: "Waiting",      value: "Waiting",             img: "/project-general-images/opportunity.png" },
  { key: "presentation", label: "Presentation", value: "Presentation Draft",  img: "/project-general-images/presentation.png" },
  { key: "estimate",     label: "Estimate",     value: "Estimate Draft",      img: "/project-general-images/estimate.png" },
  { key: "sales-order",  label: "Sales Order",  value: "Agreement Pending",   img: "/project-general-images/sales-order.png" },
];

const StagePicker = ({ value, onChange, getWorkflowLabel }) => (
  <Box className="stage-tiles" role="radiogroup" aria-label="Starting Stage">
    {STAGE_CHOICES.map(opt => {
      const selected = value === opt.value;
      return (
        <button
          type="button"
          key={opt.key}
          className={`stage-tile${selected ? " selected" : ""}`}
          onClick={() => onChange(opt.value)}
          role="radio"
          aria-checked={selected}
        >
          <img src={opt.img} alt="" aria-hidden="true" />
          <span className="stage-label">
            {getWorkflowLabel ? getWorkflowLabel('SO', opt.value) : opt.label}
          </span>
          {selected && <span className="stage-check">✓</span>}
        </button>
      );
    })}
  </Box>
);

const STORE_GUIDANCE = {
  "pop-up-store": {
    severity: "warning",
    title: "Pop-Up Store conversion",
    body: "Typically you create ONE project after the store closes, grouping the store’s orders into a single production workflow."
  },
  "company-store": {
    severity: "info",
    title: "Company Store conversion",
    body: "Company stores can run continuously — create a project for a specific run (bulk drop, replenishment, seasonal batch, etc.)."
  },
  "program": {
    severity: "info",
    title: "Program Store conversion",
    body: "Program stores are long-term. Create a project that can be reused for recurring orders, replenishment, and stock builds."
  }
};

const CONTEXT_GUIDANCE = {
  default: {
    severity: "info",
    title: "New Sales Project",
    body: "Create a sales project using the standard project workflow. Choose the customer, project details, starting stage, and assigned users."
  },
  crmPipeline: null,
  companyStore: STORE_GUIDANCE["company-store"],
};

export default function CreateProjectModal({
  open,
  onClose,
  onCreated,
  customers = [],
  context = "default",
  // optional store context:
  storeType,
  storeName,
  storeUrl,
  lockedCustomerId, // if store already has an attached customer you want locked-in
  defaultCustomerId,
  createdFrom,
  leadOrigin,
}) {
  let { instanceUniqueId } = useParams();
  const { authState } = useContext(AuthContext);
  const { getWorkflowLabel } = useWorkflowDisplaySettings(instanceUniqueId);

  const [assignedUsers, setAssignedUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [nextProjectNumber, setNextProjectNumber] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [clientContacts, setClientContacts] = useState([]);
  const [clientContactsLoading, setClientContactsLoading] = useState(false);
  const [clientContactsError, setClientContactsError] = useState("");
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactSaving, setContactSaving] = useState(false);
  const contactRequestIdRef = useRef(0);

  const guidance = STORE_GUIDANCE[storeType] || (
    Object.prototype.hasOwnProperty.call(CONTEXT_GUIDANCE, context)
      ? CONTEXT_GUIDANCE[context]
      : CONTEXT_GUIDANCE.default
  );

  useEffect(() => {
    const selectedId = lockedCustomerId || defaultCustomerId;
    if (!selectedId) {
      setSelectedCustomer(null);
      return;
    }
    const defaultCustomer = customers.find((customer) => Number(customer?.id) === Number(selectedId)) || null;
    setSelectedCustomer(defaultCustomer);
  }, [customers, lockedCustomerId, defaultCustomerId]);

  const initialValues = useMemo(() => {
    const defaultCustomer = customers.find((customer) => Number(customer?.id) === Number(lockedCustomerId || defaultCustomerId));
    const prefillName =
      storeName ? `${storeName} — Project` : "";

    const baseEvent =
      storeType ? (storeType === "program" ? "Program" : "Online store") : "Company branding";
    const defaultSite =
      Array.isArray(defaultCustomer?.websites) && defaultCustomer.websites.length > 0
        ? defaultCustomer.websites[0]
        : storeUrl || "https://";

    return {
      order_id: "100",
      attachedCustomers: lockedCustomerId ? [lockedCustomerId] : (defaultCustomerId ? [defaultCustomerId] : []),
      clientContactId: "",
      email: defaultCustomer?.emailAddress || "",
      phone: defaultCustomer?.phoneNumber || "",
      total: "",
      projectName: prefillName,
      eventType: baseEvent,
      password: "",
      items: [],
      siteURL: defaultSite,
      order_status: "Quote Requested",
    };
  }, [customers, storeName, storeUrl, storeType, lockedCustomerId, defaultCustomerId]);

  useEffect(() => {
    if (open) return;
    contactRequestIdRef.current += 1;
    setClientContacts([]);
    setClientContactsLoading(false);
    setClientContactsError("");
    setContactDialogOpen(false);
    setContactSaving(false);
  }, [open]);

  const loadClientContacts = useCallback(async (customerId) => {
    const requestId = ++contactRequestIdRef.current;
    setClientContacts([]);
    setClientContactsError("");

    if (!customerId) {
      setClientContactsLoading(false);
      return;
    }

    setClientContactsLoading(true);
    try {
      const contacts = await fetchClientContactOptions(customerId, getClientContacts);
      if (requestId !== contactRequestIdRef.current) return;
      setClientContacts(contacts);
    } catch (error) {
      if (requestId !== contactRequestIdRef.current) return;
      console.error("Error fetching client contacts:", error);
      setClientContacts([]);
      setClientContactsError("Unable to load client contacts. You can still create this project with N/A.");
    } finally {
      if (requestId === contactRequestIdRef.current) {
        setClientContactsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const selectedId = lockedCustomerId || defaultCustomerId;
    if (selectedId) loadClientContacts(selectedId);
  }, [open, lockedCustomerId, defaultCustomerId, loadClientContacts]);

  useEffect(() => {
    if (!authState?.id) return;

    axios
      .get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/basicinfo/${authState.id}`)
      .then(({ data }) => {
        const rec = data.recommendedUsers || [];
        const me = { id: authState.id, role: authState.role, first_name: authState.first_name, last_name: authState.last_name };
        const initial = rec.some(u => u.id === authState.id) ? rec : [me, ...rec];
        setAssignedUsers(initial);
      })
      .catch((err) => console.error("Error fetching recommendedUsers:", err));
  }, [authState]);

  useEffect(() => {
    if (!open || !instanceUniqueId) return;
    let active = true;

    const fetchNextNumber = async () => {
      try {
        const instanceRes = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byinstanceUniqueId/${instanceUniqueId}`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );
        const instanceId = instanceRes?.data?.id ?? null;
        if (!instanceId || !active) return;

        const nextRes = await getNextProjectNumber(instanceId);
        if (active) {
          setNextProjectNumber(nextRes?.nextProjectNumber ?? null);
        }
      } catch (error) {
        console.error("Error fetching next project number:", error);
        if (active) setNextProjectNumber(null);
      }
    };

    fetchNextNumber();
    return () => {
      active = false;
    };
  }, [open, instanceUniqueId]);

  const validationSchema = Yup.object({
    projectName: Yup.string().trim().min(3, "Name is too short").required("Project Name is required"),
    email: Yup.string().trim().email("Invalid email").required("Customer email is required to create a project."),
    eventType: Yup.string().required("Event Type is required"),
    attachedCustomers: Yup.array().min(1, "Customer is required"),
    order_status: Yup.string().required("Order status is required"),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (!assignedUsers.length) {
        alert("Assigned users is required");
        setSubmitting(false);
        return;
      }

      const selectedCustomerId = Array.isArray(values.attachedCustomers) ? values.attachedCustomers[0] : null;
      const customerForGuard =
        selectedCustomer ||
        customers.find((customer) => Number(customer?.id) === Number(selectedCustomerId)) ||
        null;
      if (isCreditHoldCustomer(customerForGuard)) {
        const message = "This customer is on Credit Hold and cannot be used to create a project.";
        setSubmitError(message);
        alert(message);
        setSubmitting(false);
        return;
      }

      setSubmitError("");
      setSaving(true);

      const payload = {
        data: {
          ...withOptionalClientContact(values),
          instanceUniqueId,
          assigned_to: assignedUsers,
          ...(createdFrom ? { createdFrom } : {}),
          ...(leadOrigin ? { leadOrigin } : {}),
          // optional context for auditing later:
          storeType: storeType || null,
          storeName: storeName || null,
          storeUrl: storeUrl || null,
        },
      };

      const res = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/orders/create-order`,
        payload,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );

      onCreated?.(res.data);
      resetForm();
      onClose?.();
    } catch (err) {
      console.error("Error creating project", err);
      const message = err?.response?.data?.message || "Failed to create project. Check required customer and project fields.";
      setSubmitError(message);
      alert(message);
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (saving && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
        onClose?.();
      }}
      disableEscapeKeyDown={saving}
      className="create-order-dialog"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className="create-order-dialog-title">
        Create New Project
      </DialogTitle>

      <DialogContent className="create-order-dialog-content">
        {guidance ? (
          <Alert severity={guidance.severity} sx={{ mb: 2 }}>
            <b>{guidance.title}:</b> {guidance.body}
          </Alert>
        ) : null}
        {submitError ? <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert> : null}

        <Box sx={{ mb: 2 }}>
          <TextField
            label="Next Project #"
            value={nextProjectNumber ?? "—"}
            fullWidth
            InputProps={{ readOnly: true }}
          />
        </Box>

        <Formik initialValues={initialValues} enableReinitialize validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ values, setFieldValue, isSubmitting, errors, touched }) => {
            const hasCustomer = Array.isArray(values.attachedCustomers) && values.attachedCustomers.length > 0;
            const selectedCustomerId = values.attachedCustomers?.[0] || null;
            const selectedBusinessName = getCustomerBusinessName(
              selectedCustomer ||
              customers.find((customer) => Number(customer?.id) === Number(selectedCustomerId))
            );
            const handleCreateContact = async (payload) => {
              if (!selectedCustomerId) return;
              try {
                setContactSaving(true);
                const response = await createCustomerContact(selectedCustomerId, payload);
                const createdOption = normalizeCreatedClientContact(response?.data?.contact);
                if (!createdOption) throw new Error("Created contact did not include an id");
                setClientContacts((previous) => [
                  ...previous.filter(
                    (contact) => String(contact.contactId) !== String(createdOption.contactId)
                  ),
                  createdOption,
                ]);
                setFieldValue("clientContactId", createdOption.contactId);
                setContactDialogOpen(false);
              } catch (error) {
                const message = error?.response?.data?.message || "Unable to create the customer contact.";
                setClientContactsError(message);
                alert(message);
              } finally {
                setContactSaving(false);
              }
            };

            return (
              <Form className="create-order-form">
                {/* Customer Select */}
                <div className="create-order-field-container">
                  <CustomerSearchSelectAsync
                    customers={customers}
                    value={values.attachedCustomers?.[0] ?? ""}
                    instanceUniqueId={instanceUniqueId}
                    label="Search Account"
                    disabled={!!lockedCustomerId}
                    onSelectCustomer={(cust) => {
                      setSubmitError("");
                      setSelectedCustomer(cust || null);
                      if (cust) {
                        setFieldValue("email", cust.emailAddress || "");
                        setFieldValue("phone", cust.phoneNumber || "");
                        const firstSite =
                          Array.isArray(cust.websites) && cust.websites.length > 0
                            ? cust.websites[0]
                            : "";
                        setFieldValue("siteURL", firstSite || "https://");
                      } else {
                        setFieldValue("email", "");
                        setFieldValue("phone", "");
                        setFieldValue("siteURL", "https://");
                      }
                    }}
                    onChange={(custId) => {
                      if (lockedCustomerId) return;
                      const nextValues = getCustomerChangeValues(custId);
                      setFieldValue("attachedCustomers", nextValues.attachedCustomers);
                      setFieldValue("clientContactId", nextValues.clientContactId);
                      loadClientContacts(custId);
                    }}
                  />
                  <ErrorMessage name="attachedCustomers" component="div" className="error" />
                </div>

                {hasCustomer && (
                  <>
                    <ProjectCustomerEmailNotice
                      email={values.email}
                      customerId={selectedCustomerId}
                      instanceUniqueId={instanceUniqueId}
                    />
                    <div className="create-order-field-container">
                      <Field as={TextField} label="Project Name" name="projectName" fullWidth className="create-order-input"
                        error={Boolean(touched.projectName && errors.projectName)}
                        helperText={<ErrorMessage name="projectName" />}
                      />
                    </div>

                    <div className="create-order-field-container">
                      <FormControl fullWidth>
                        <InputLabel id="eventType-label">Event Type</InputLabel>
                        <Field as={Select} labelId="eventType-label" label="Event Type" name="eventType" fullWidth
                          MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
                        >
                          {EVENT_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>{t}</MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                      <ErrorMessage name="eventType" component="div" className="create-order-error" />
                    </div>

                    <div className="create-order-field-container">
                      <label className="create-order-label">Starting Stage</label>
                      <StagePicker value={values.order_status} onChange={(val) => setFieldValue("order_status", val)} getWorkflowLabel={getWorkflowLabel} />
                      <ErrorMessage name="order_status" component="div" className="create-order-error" />
                    </div>

                    <Accordion sx={{ mt: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={600}>Client Contact</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Button
                          type="button"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setContactDialogOpen(true)}
                          sx={{ mb: 1.5 }}
                        >
                          Add contact to {selectedBusinessName}
                        </Button>
                        <FormControl fullWidth disabled={clientContactsLoading}>
                          <InputLabel id="new-project-client-contact-label">Client Contact</InputLabel>
                          <Select
                            labelId="new-project-client-contact-label"
                            id="new-project-client-contact"
                            name="clientContactId"
                            label="Client Contact"
                            value={values.clientContactId || ""}
                            onChange={(event) => setFieldValue("clientContactId", event.target.value)}
                            endAdornment={clientContactsLoading ? <CircularProgress size={18} sx={{ mr: 4 }} /> : null}
                          >
                            <MenuItem value="">N/A</MenuItem>
                            {clientContacts.map((contact) => (
                              <MenuItem key={contact.contactId} value={contact.contactId}>
                                {contact.displayLabel}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {!clientContactsLoading && !clientContactsError && clientContacts.length === 0 ? (
                          <FormHelperText>No client contacts available for this customer.</FormHelperText>
                        ) : null}
                        {clientContactsError ? (
                          <Alert severity="warning" sx={{ mt: 1 }}>{clientContactsError}</Alert>
                        ) : null}
                        <CustomerContactDialog
                          open={contactDialogOpen}
                          loading={contactSaving}
                          onClose={() => setContactDialogOpen(false)}
                          onSubmit={handleCreateContact}
                          targetLabel={selectedBusinessName}
                        />
                      </AccordionDetails>
                    </Accordion>

                    <Accordion sx={{ mt: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight={600}>Assign users</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <div className="create-order-field-container">
                          <OrderUserAssignment
                            orderId=""
                            initialAssignedUsers={assignedUsers}
                            onChangeAssignedUsers={(newAssignedUsers) => setAssignedUsers(newAssignedUsers)}
                          />
                        </div>
                      </AccordionDetails>
                    </Accordion>

                    <DialogActions className="create-order-dialog-actions">
                      <Button onClick={onClose} color="secondary" disabled={saving}>
                        Cancel
                      </Button>
                      <Button type="submit" color="primary" disabled={saving || isSubmitting}>
                        {saving || isSubmitting ? <ButtonLoader /> : "Create Project"}
                      </Button>
                    </DialogActions>
                  </>
                )}
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
